import { Action, Middleware } from 'redux';
import { service } from '@src/service/index.js';
import { VALIDATE, ERROR as ERROR$auth } from '@src/store/auth.js';
import { OCCL, ERROR as ERROR$config } from '@src/store/config.js';
import {
  LOAD,
  SAVE,
  ERROR as ERROR$consents,
  TOGGLE,
  ONECLICK,
  DENY,
  CANCEL_PAGE,
  CANCEL_EDIT_MODE,
  SET_EDIT_ACTIVE_GROUP_IND,
  TOGGLE_MERGE_ACCEPTED,
  CANCEL_MICROFRONTEND,
} from '@src/store/consents.js';
import type { RootState } from '@src/store/index.js';
import { ConsentGroup } from '@src/types/common.js';

const fired: string[] = []; // must be reset at storage creation time
const firedTracking: string[] = [];

type EventMapping = {
  name: string;
  message: string;
  description?: string;
  condition?: (
    act: Action<string> & { consentGroups: ConsentGroup[], groupInd: number },
    state: RootState
  ) => boolean;
  once?: boolean;
  level?: string;
  properties?: (
    action: Action<string> & { error?: unknown; consentGroups: ConsentGroup[] },
    { config }: RootState
  ) => Record<string, unknown>;
};

export const events: EventMapping[] = [
  {
    // read by doc generator !!
    name: 'config/INIT', // re-used for performance measurements !!
    message: 'micro-frontend is initializing',
    description:
      'The micro-frontend is initializing. Technically, this is immediately after the custom element has been inserted into the DOM and its connectedCallback has been called by the browser. This event can be used for performance measurements.',
    condition: ({ type }) => type === 'CONNECTED',
    once: true,
  },
  {
    name: VALIDATE,
    message: 'token validation successful',
    description: 'Token validation was successful.',
  },
  {
    name: ERROR$auth,
    message: 'token validation failed',
    description: 'Token validation failed.',
    level: 'error',
    properties: ({ error }) => ({ error }),
  },
  {
    name: 'TRACKING',
    message: 'token validation failed',
    description: 'Token validation failed.',
    level: 'error',
    once: true,
    properties: (_action, { config }) => {
      const origin = config ? config.lifecycle : undefined;
      return {
        data: {
          error_message: 'token validation failed',
          event: 'comsError',
          origin,
        },
      };
    },
    condition: ({ type }) => type === ERROR$auth,
  },
  {
    name: LOAD,
    message: 'loading consents successful',
    description:
      'Loading consents was successful. This event is also issued when the consents are loaded from session storage (which in some scenarios they are).',
  },
  {
    name: SAVE,
    message: 'saving consents successful',
    description: 'Saving consents was successful. Remark: In case MFE is displaying buttons best practise is to reload the MFE if this event is received to disable the save-button again',
  },
  {
    name: TOGGLE_MERGE_ACCEPTED,
    message: 'user change state of accept merge accounts checkbox',
    description: 'User have clicked on accept merge accounts checkbox. Current state is saved in property isChecked',
    properties: (_action, { consents: { mergeAccepted } }) => ({ isChecked: mergeAccepted }),
  },
  {
    name: ERROR$consents,
    message: 'loading or saving consents/customer attributes failed',
    description: 'Loading or saving consents or customer attributes failed.',
    level: 'error',
    properties: ({ error }) => ({ error }),
  },
  {
    name: 'TRACKING',
    message: 'loading or saving consents failed',
    level: 'error',
    properties: (_action, { config }) => {
      const origin = config ? config.lifecycle : undefined;
      return {
        data: {
          error_message: 'loading or saving consents failed',
          event: 'comsError',
          origin,
        },
      };
    },
    condition: ({ type }) => type === ERROR$consents,
  },
  {
    name: 'consents/EMPTY',
    message: 'there are no consents rendered',
    description:
      'There are no consents to be rendered. The micro-frontend will not visually render anything.',
    condition: ({ type, consentGroups = [] }) => type === LOAD && consentGroups.length === 0,
    once: true,
  },
  {
    name: 'consents/CHANGE',
    message: 'consents were changed',
    description:
      'The user changed a consent. This event does *not* provide any further details *which* consents where changed. It is issued at every change, i.e., multiple times. This event is indicating that consents were changed, e.g., in order to enable an external save button.',
    condition: ({ type }) => [TOGGLE, ONECLICK, DENY].includes(type),
  },
  {
    name: 'action/CHANGE',
    message: 'change consents started',
    description: 'The user started editing consents in multi-edit mode.',
    condition: ({ type, groupInd }) => type === SET_EDIT_ACTIVE_GROUP_IND && groupInd >= 0,
  },
  {
    name: 'action/CANCEL',
    message: 'cancel action invoked',
    description:
      'The user clicked the cancel button either in multi-edit, button or cattr mode.',
    condition: ({ type }) => [CANCEL_PAGE, CANCEL_EDIT_MODE].includes(type),
  },
  {
    name: 'action/SAVE',
    message: 'save action invoked',
    description:
      'The user clicked the save button either in multi-edit mode or in button mode or the save method was called.',
    condition: ({ type }) => type === SAVE,
  },
  {
    name: 'action/CANCEL_MICROFRONTEND',
    message: 'cancel microfrontend action invoked',
    description:
      'The user clicked the back ("Zurück") button when in viewing mode. The microfrontend should be closed by the integrating party if this event occurs.',
    condition: ({ type }) => [CANCEL_MICROFRONTEND].includes(type),
  },
  {
    name: 'TRACKING',
    message: 'micro-frontend has failed',
    level: 'error',
    properties: (_action, { config }) => {
      const origin = config ? config.lifecycle : undefined;
      return {
        data: {
          error_message: 'micro-frontend has failed',
          event: 'comsError',
          origin,
        },
      };
    },
    condition: ({ type }, { consents }) => {
      const consentGroups = consents ? consents.consentGroups : undefined;
      return (
        type === ERROR$config
        || type === ERROR$auth
        || (type === ERROR$consents && !consentGroups)
      );
    },
    once: true,
  },
  {
    name: 'load/READY',
    message: 'micro-frontend is ready',
    description:
      'The micro-frontend has successfully loaded all required resources (including OCCL). This is indicating that a loading spinner (if displayed by the integrating portal) shall now be removed. It is guaranteed that either `load/READY` or `load/ERROR` is issued – exactly once.',
    condition: ({ type }, { config, consents }) => {
      const occlReady = config ? config.occlReady : undefined;
      const consentGroups = consents ? consents.consentGroups : undefined;

      if (type === LOAD && !config.occluri && !!consentGroups) {
        return true;
      }

      return (
        ((type === OCCL && fired.includes(LOAD)) || type === LOAD)
        && !!occlReady
        && !!consentGroups
      );
    },
    once: true,
  },
  {
    name: 'load/ERROR',
    message: 'micro-frontend has failed',
    description: 'The micro-frontend has failed. This might be due to a misconfiguration, a failure to load a required resource (including OCCL/dsOne) or usage of `<tef-coms>` more than once on the page.  It will not visually render anything.  This is indicating that a loading spinner (if displayed by the integrating portal) shall now be removed.  It is guaranteed that either `load/READY` or `load/ERROR` is issued – exactly once.',
    condition: ({ type }, { consents }) => {
      const consentGroups = consents ? consents.consentGroups : undefined;
      return (
        type === ERROR$config
        || type === ERROR$auth
        || (type === ERROR$consents && !consentGroups)
      );
    },
    level: 'error',
    properties: ({ error }) => ({ error }),
    once: true,
  },
];

export const eventsMiddleware: Middleware<unknown, RootState> = (store) => {
  const nameEqualsType = (name: string) => ({ type }: Action<string>) => name === type;
  const none = () => ({});
  return (next) => (_action: unknown) => {
    const action = _action as Action<string> & {
      groupInd: number;
      consentGroups: ConsentGroup[];
    };

    const { type } = action;
    if (type === 'RESET') {
      // this is required for unit tests because createStore()
      // is only executed once because store is a singleton
      fired.length = 0; // empty array
      firedTracking.length = 0;
    }
    const ret = next(action);
    const state = store.getState();
    events
      .filter(({ name, condition = nameEqualsType(name) }) => condition(
        action,
        state,
      ))
      .forEach(
        ({
          name,
          level = 'info',
          message,
          properties = none,
          once = false,
        }) => {
          if (once && (fired.includes(name) || firedTracking.includes(name))) return;
          const details = properties(action, state);
          if (!details) return;
          service.fireEvent(name, { level, message, ...details });
          fired.push(name);
          firedTracking.push(name);
        },
      );
    return ret;
  };
};
